/**
 * Modo suite: Finance corriendo DENTRO de otro producto (ítem 096).
 *
 * ── EL DEFECTO QUE ESTO ARREGLA ─────────────────────────────────────────────
 *
 * `EmbedShell` monta UN componente de una lista blanca. Eso parecía una
 * frontera y no lo era: en cuanto la pantalla de adentro navega —un enlace, un
 * `navigate()`, un botón «ver detalle»— React Router sale de `/embed/:pantalla`
 * y monta `<Layout>`. Resultado: **el producto entero adentro del iframe**, con
 * su propia barra lateral al lado de la de Unity, su encabezado, su botón de
 * «Salir», y el alto congelado porque el armazón usa `h-screen`.
 *
 * ── DONDE VA LA FRONTERA, Y ESTO ES TODO EL ITEM ────────────────────────────
 *
 * **Donde se dibuja el armazón, no en una lista de destinos permitidos.**
 *
 * Una lista blanca obliga a enumerar cada pantalla que puede abrirse, y falla
 * por omisión: la pantalla número veinte que alguien agregue mañana no está en
 * la lista, y el escape vuelve. Un interruptor que lee el armazón cubre **todas
 * las rutas que existen y todas las que alguien agregue después**, porque el
 * armazón es uno y las rutas son ciento.
 *
 * ── POR QUE `sessionStorage` Y NO UN ESTADO DE REACT ────────────────────────
 *
 * Porque tiene que sobrevivir a la navegación de adentro, que es justamente lo
 * que lo activa. Un estado de React se pierde cuando el router desmonta
 * `EmbedShell` para montar `Layout` — o sea, exactamente en el momento en que
 * hace falta.
 *
 * Y es `sessionStorage` y no `localStorage` porque es de ESTA pestaña: con
 * `localStorage`, abrir Finance en una pestaña aparte después de haber usado el
 * embebido le quitaría el menú sin motivo.
 *
 * ── LA COMPROBACION DE QUE ESTAMOS ENMARCADOS NO ES OPCIONAL ───────────────
 *
 * `window.parent !== window`. Sin eso, entrar a `/embed/catalogo-cuentas` a
 * mano en una pestaña dejaría a Finance sin menú y sin forma de salir, y
 * parecería que el producto se rompió.
 */

const LLAVE = 'finance360:modoSuite';

/** Si esta ventana está dentro de otra. En un `try` porque un marco de otro origen puede lanzar. */
function estamosEnmarcados () {
  try {
    return typeof window !== 'undefined' && window.parent && window.parent !== window;
  } catch {
    // Si no se puede saber, se asume que sí: es lo que ocurre cuando el padre es
    // de otro origen, que es precisamente nuestro caso.
    return true;
  }
}

function leer (llave) {
  try { return window.sessionStorage.getItem(llave); } catch { return null; }
}

/**
 * Enciende el modo suite. Lo llama `EmbedShell` al montarse.
 *
 * @param {string} origen  el origen del anfitrión, para poder hablarle desde
 *                         cualquier pantalla de adentro y no solo desde la cáscara.
 */
export function activarModoSuite (origen = '') {
  if (!estamosEnmarcados()) return false;
  try {
    window.sessionStorage.setItem(LLAVE, '1');
    if (origen) window.sessionStorage.setItem(`${LLAVE}:origen`, origen);
  } catch { /* una pestaña privada sin almacenamiento: el modo dura lo que la cáscara */ }
  return true;
}

/** Si Finance se está mostrando dentro de otro producto. */
export function enModoSuite () {
  return estamosEnmarcados() && leer(LLAVE) === '1';
}

/** El origen del anfitrión, si se fijó. Para `postMessage` desde cualquier pantalla. */
export function origenDelAnfitrion () {
  return leer(`${LLAVE}:origen`) || null;
}

/** Se apaga al cerrar la sesión embebida. */
export function apagarModoSuite () {
  try {
    window.sessionStorage.removeItem(LLAVE);
    window.sessionStorage.removeItem(`${LLAVE}:origen`);
  } catch { /* nada que apagar */ }
}

/**
 * Le habla al anfitrión desde cualquier pantalla de adentro.
 *
 * Devuelve si pudo. No lanza: una pantalla de Finance no se puede caer porque el
 * anfitrión no esté escuchando — tiene que seguir funcionando igual abierta
 * sola.
 */
export function avisarAlAnfitrion (tipo, datos = {}) {
  const origen = origenDelAnfitrion();
  if (!origen || !enModoSuite()) return false;
  try {
    window.parent.postMessage({ tipo, ...datos }, origen);
    return true;
  } catch {
    return false;
  }
}

/**
 * Mide un elemento y le avisa al anfitrión cuánto mide, mientras exista.
 *
 * ── POR QUE ESTO VIVE ACA Y NO EN LA CASCARA ────────────────────────────────
 *
 * La cáscara (`EmbedShell`) se **desmonta** en cuanto se navega adentro, y era
 * la única que medía. Así que después del primer clic nadie informaba el alto y
 * el marco se quedaba con el de la pantalla de entrada: el contenido salía
 * cortado o con media pantalla en blanco. Es el «alto congelado» del ítem 096,
 * y la causa no era el iframe sino que el medidor se iba.
 *
 * Ahora lo usan los dos: la cáscara mientras está, y el armazón en modo suite
 * de ahí en adelante.
 *
 * Devuelve una función para dejar de medir.
 *
 * @param {HTMLElement} elemento
 */
export function medirYAvisarElAlto (elemento) {
  if (!elemento || typeof ResizeObserver === 'undefined') return () => {};
  let ultimo = 0;
  const medir = () => {
    const alto = Math.ceil(elemento.scrollHeight || 0);
    // Un umbral de 8 px: sin él, cada animación de una fila dispara un mensaje
    // y el anfitrión redibuja el marco en bucle.
    if (alto && Math.abs(alto - ultimo) > 8) {
      ultimo = alto;
      avisarAlAnfitrion('embed:alto', { alto });
    }
  };
  const observador = new ResizeObserver(medir);
  observador.observe(elemento);
  medir();
  return () => observador.disconnect();
}
